import React, { useEffect, useState } from 'react'

import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { invoiceAdvanceInvoiceCount } from 'provider/queries/hasuraQueries'
import {
  useTranslate,
  TextField,
  useQueryWithStore,
  Error,
  Loading,
} from 'react-admin'
import { post as getFromHasura } from 'utils/axios'

import {
  lighterGray,
  normalGray,
  darkGray,
  white,
  lightergray4,
} from '../../constants/colorConstants'
import { EventCreditLineEmpty } from '../emptyViews/eventCreditLineEmptyView'
import { CreditLineStatusDescriptionField } from '../fields/creditLineStatusDescriptionField'
import { CreditLineStatusField } from '../fields/creditLineStatusField'
import { CurrencyField } from '../fields/currencyField'
import { DaysTextField } from '../fields/daysTextField'
import { PercentTextField } from '../fields/percentTextField'
import { SimpleCreditLineList } from '../lists/eventCreditLineList'

const useStyles = makeStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: lighterGray,
    paddingTop: '39px',
    paddingBottom: '42px',
    paddingLeft: '10px',
    paddingRight: '40px',
    marginTop: '-6px',
    marginBottom: '-6px',
    marginLeft: '-5px',
    marginRight: '-15px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingLeft: '99px',
    paddingRight: '38px',
  },
  infoColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingLeft: '22px',
    paddingRight: '0px',
    whiteSpace: 'nowrap',
  },
  infoColumnVertical: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    whiteSpace: 'nowrap',
  },
  tablePanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginLeft: '100px',
  },
  tableColumn: {
    backgroundColor: white,
    width: '768px',
    borderRadius: '8px',
  },
  title: {
    fontSize: '18px',
    lineHeight: '21.6px',
    ...theme.mundi.font.lato700Normal,
    marginBottom: '31px',
    width: '200px',
  },
  titleTable: {
    fontSize: '18px',
    lineHeight: '21.6px',
    ...theme.mundi.font.lato700Normal,
    marginBottom: '31px',
    marginTop: '25px',
    marginLeft: '24px',
  },
  fieldName: {
    fontSize: '12px',
    lineHeight: '14.6px',
    ...theme.mundi.font.roboto400Normal,
    color: normalGray,
    marginBottom: '8px',
  },
  fieldValue: {
    fontSize: '14px',
    lineHeight: '16.41px',
    ...theme.mundi.font.roboto400Normal,
    color: darkGray,
    marginBottom: '32px',
  },
  eventCreditLineEmpty: {
    minWidth: '729px',
    background: white,
    border: `1px solid ${lightergray4}`,
    boxSizing: 'border-box',
    borderRadius: '8px',
    marginLeft: '70px',
  },
  field: {
    ...theme.mundi.font.roboto400Normal,
    whiteSpace: 'nowrap',
    fontSize: '13px',
    lineHeight: '15.23px',
    textTransform: 'capitalize',
  },
}))

export const EventsCreditLineExpandableView = (props) => {
  const { record } = props
  const classes = useStyles()
  const translate = useTranslate()
  const [statusError, setStatusError] = useState()
  const [invoiceCount, setInvoiceCount] = useState(-1)

  const { data, error, loading } = useQueryWithStore({
    type: 'getList',
    resource: 'invoices',
    payload: {
      where: {
        credit_line_id: { _eq: record.id },
        paid_at: { _is_null: true },
        advance_date: { _is_null: false },
      },
    },
  })

  async function fetchCount() {
    const invoiceCount = await getFromHasura({
      payload: invoiceAdvanceInvoiceCount,
      variables: {
        credit_line_id: record.id,
      },
    })

    if (invoiceCount.errors?.length) {
      console.error(JSON.stringify(invoiceCount.errors[0]))
      setStatusError(true)
      return
    }

    setInvoiceCount(
      invoiceCount.data.invoiceadvance_view_invoice_advance_summary_aggregate
        .aggregate.count
    )
  }

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  if (error || statusError) return <Error error={error} />

  if (loading) return <Loading />

  return (
    <div className={classes.panel}>
      <div className={classes.infoRow}>
        <Typography className={classes.title}>
          {props.record.importerName}
        </Typography>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.lineAmount')}
          </Typography>
          <CurrencyField
            record={props.record}
            source="maxValue"
            className={classes.fieldValue}
          />
        </div>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.currentlyAvailable')}
          </Typography>
          <CurrencyField
            record={props.record}
            source="available"
            className={classes.fieldValue}
          />
        </div>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.maxTerm')}
          </Typography>
          <DaysTextField
            record={props.record}
            className={classes.field}
            source="maxDays"
          />
        </div>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.maxAdvanceRate')}
          </Typography>
          <PercentTextField
            record={props.record}
            className={classes.field}
            source="advanceRate"
            multiplier={100}
          />
        </div>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.monthlyCost')}
          </Typography>
          <PercentTextField
            record={props.record}
            className={classes.field}
            source="grossRate"
            multiplier={100}
          />
        </div>
        <div className={classes.infoColumn}>
          <Typography className={classes.fieldName}>
            {translate('resources.creditlines.creditline.gracePeriod')}
          </Typography>
          <DaysTextField
            record={props.record}
            className={classes.field}
            source="gracePeriod"
          />
        </div>
      </div>
      <div className={classes.infoRow}>
        <div className={classes.infoColumnVertical}>
          <CreditLineStatusField source="status" record={props.record} />
          <CreditLineStatusDescriptionField
            source="status"
            record={props.record}
          />
          <Typography className={classes.field}>
            {translate(
              'resources.invoices.fields.destinationCountry'
            ).toLowerCase()}{' '}
            <TextField source="buyerCountry" record={props.record} />
          </Typography>
        </div>
        <div className={classes.tablePanel}>
          {invoiceCount === 0 && (
            <EventCreditLineEmpty className={classes.eventCreditLineEmpty} />
          )}
          {invoiceCount > 0 && (
            <>
              <Typography className={classes.titleTable}>
                {translate(
                  'resources.creditlines.messages.currentlyAdvancedInvoices'
                ).replace('<<N>>', data.length)}
              </Typography>
              <div className={classes.tableColumn}>
                <SimpleCreditLineList
                  items={data}
                  invoiceCount={invoiceCount}
                  {...props}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
